import * as React from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom-v5-compat'

import { useQueryParam } from '~publish/hooks/useQueryParam'
import { commentsChannel } from '~publish/legacy/routes'

import {
  DATE_RANGES,
  type DateRange,
  DEFAULT_STATUS,
  type Filters,
  type SortDirection,
  type UseFiltersReturn,
  type FilterStatus,
} from './types'

/**
 * A convenience hook to access just the comment status from useFilters.
 * This makes it easier to consume the status in mutation hooks and other components
 * without being coupled to the full useFilters return shape.
 */
export const useCommentStatus = (): FilterStatus => {
  const { filters } = useFilters()
  return filters.status || DEFAULT_STATUS
}

export const useFilters = (): UseFiltersReturn => {
  const dispatch = useDispatch()
  const { channelId } = useParams<{ channelId: string }>()
  const [statusParam, setStatus] = useQueryParam<FilterStatus>('status')
  const status = statusParam || DEFAULT_STATUS

  const [sortDirection, setSortDirection] = useQueryParam<SortDirection>('sort')
  const [publishedAt, setPublishedAt] = useQueryParam<DateRange>('publishedAt')
  const [dateStart, setDateStart] = React.useState<string | undefined>()
  const [dateEnd, setDateEnd] = React.useState<string | undefined>()

  // Memoize the date range calculation to avoid unnecessary recalculations
  React.useEffect(() => {
    if (publishedAt && publishedAt !== 'alltime') {
      const days = DATE_RANGES[publishedAt]
      if (days) {
        const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
        setDateStart(startDate.toISOString())
        setDateEnd(new Date().toISOString())
      }
    } else {
      setDateStart(undefined)
      setDateEnd(undefined)
    }
  }, [publishedAt])

  const clearFilters = React.useCallback((): void => {
    if (!channelId) return

    // Reset state
    setSortDirection(undefined)
    setPublishedAt(undefined)

    // TO DO: update use of location change in Redux
    // https://linear.app/buffer/issue/CHAN-5410/redux-navigation-audit-document-use-of-push-in-middleware-and-location
    dispatch(commentsChannel.goToBaseWithStatus({ channelId })) // we are not clearing the status
  }, [channelId, setSortDirection, setPublishedAt, dispatch])

  const hasActiveFilters = React.useCallback((): boolean => {
    return Boolean(sortDirection || publishedAt || dateStart || dateEnd)
  }, [sortDirection, publishedAt, dateStart, dateEnd])

  // Memoize the filter state to prevent unnecessary re-renders
  const filters: Filters = React.useMemo(
    () => ({
      status: status || DEFAULT_STATUS,
      sortDirection,
      publishedAt,
      dateStart,
      dateEnd,
    }),
    [status, sortDirection, publishedAt, dateStart, dateEnd],
  )

  return {
    filters,
    actions: {
      setStatus,
      setSortDirection,
      setPublishedAt,
    },
    utils: {
      clearFilters,
      hasActiveFilters,
    },
  }
}
